import { BlurView, type BlurViewProps } from 'expo-blur';
import { cssInterop } from 'nativewind';
import React, { forwardRef } from 'react';
import { Platform, View } from 'react-native';

// SDK 56+ ships expo-blur precompiled to react/jsx-runtime, so its inner View no longer
// passes through the nativewind JSX transform — className must be mapped explicitly.
cssInterop(BlurView, { className: 'style' });

/**
 * A wrapper around Expo's BlurView with safe Android defaults.
 */
export const SafeBlurView = forwardRef<BlurView, BlurViewProps>((props, ref) => {
  const { blurMethod, style, children, ...otherProps } = props;
  const className = (props as { className?: string }).className;
  const hasAndroidBlurTarget = Platform.OS === 'android' && !!otherProps.blurTarget;

  const finalBlurMethod =
    Platform.OS === 'android'
      ? hasAndroidBlurTarget
        ? (blurMethod ?? 'dimezisBlurViewSdk31Plus')
        : 'none'
      : blurMethod;

  // On Android when intensity is 0, BlurView ends up effectively transparent.
  // Render a plain View fallback so callers can provide a themed background via className/style.
  if (Platform.OS === 'android' && (otherProps.intensity ?? 0) === 0) {
    return (
      <View className={className} style={style}>
        {children}
      </View>
    );
  }

  return (
    <BlurView
      ref={ref}
      {...otherProps}
      style={style}
      className={className}
      blurMethod={finalBlurMethod}>
      {children}
    </BlurView>
  );
});

SafeBlurView.displayName = 'SafeBlurView';
